/**
 * Helpers for turning an Axios error into something worth showing a user.
 *
 * The API returns several different error shapes depending on which handler in
 * ExceptionHandlers.java caught the exception:
 *
 *   ErrorResponse    → { timestamp, status, error, message }
 *   RestServiceError → { code, message, errorDetails: { field: msg } }
 *   plain string     → "..."
 *
 * A common bug in this codebase was stringifying every key of the body, which turns
 * a normal error into "timestamp: ..., status: 500, error: ...". Read the known
 * fields instead, and only fall back to a field map when there genuinely is one.
 *
 * Note: an endpoint that throws a bare RuntimeException produces Spring's default
 * 500 body, which has NO message field at all — nothing can be extracted there, so
 * the fallback is what the user sees. The fix for those belongs on the server.
 */
export function extractApiMessage(error, fallback = 'Something went wrong. Please try again.') {
  const data = error?.response?.data;

  if (typeof data === 'string' && data.trim()) return data;

  if (data && typeof data === 'object') {
    if (data.message) return data.message;
    if (data.detail) return data.detail;

    const fieldMap = data.errorDetails || data.errors;
    if (fieldMap && typeof fieldMap === 'object') {
      const lines = Object.entries(fieldMap)
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
      if (lines.length) return lines.join('\n');
    }
  }

  // Network failure / timeout — no response body at all.
  if (!error?.response && error?.message) return error.message;

  return fallback;
}

/**
 * True when the failure is a "this name/number is already taken" conflict.
 *
 * 409 is what the API sends now. The message check is a safety net for older
 * deployments where these still surfaced as a 500 with the reason in the body.
 */
export function isDuplicateError(error, message = '') {
  return error?.response?.status === 409 || /already exists/i.test(message);
}
